/**
 * CRM-Sales Integration Event Types
 *
 * Defines event schemas for the mycelium event bridge between CRM and Sales Portal.
 * Follows economy of movement principles for world-class integration.
 */

import type { Opportunity, Client, SalesTransaction } from '../index';

// ============================================================================
// EVENT TYPES
// ============================================================================

export type CRMSalesEventType =
  // Opportunity Events
  | 'opportunity.created'
  | 'opportunity.updated'
  | 'opportunity.stage_changed'
  | 'opportunity.closed_won'
  | 'opportunity.closed_lost'
  | 'opportunity.assigned'
  | 'opportunity.deleted'
  // Client Events
  | 'client.created'
  | 'client.updated'
  | 'client.assigned'
  | 'client.deleted'
  // Transaction Events
  | 'transaction.created'
  | 'transaction.updated'
  | 'transaction.approved'
  | 'transaction.rejected'
  | 'transaction.paid'
  // Commission Events
  | 'commission.calculated'
  | 'commission.approved'
  | 'commission.paid'
  // Xero Sync Events
  | 'xero.invoice_synced'
  | 'xero.contact_synced'
  | 'xero.payment_synced'
  | 'xero.sync_conflict'
  | 'xero.sync_completed';

// ============================================================================
// EVENT PAYLOAD INTERFACES
// ============================================================================

export interface BaseEventPayload<T = unknown> {
  eventType: CRMSalesEventType;
  data: T;
  source: string;
  timestamp: Date;
  entityType?: string;
  entityId?: string;
  actorId?: string;
  metadata?: Record<string, unknown>;
}

// Opportunity Events
export interface OpportunityCreatedPayload extends BaseEventPayload<Opportunity> {
  eventType: 'opportunity.created';
  data: Opportunity;
}

export interface OpportunityStageChangedPayload extends BaseEventPayload {
  eventType: 'opportunity.stage_changed';
  data: {
    opportunity: Opportunity;
    oldStage: string;
    newStage: string;
    reason?: string;
  };
}

export interface OpportunityClosedWonPayload extends BaseEventPayload<Opportunity> {
  eventType: 'opportunity.closed_won';
  data: Opportunity;
}

// Client Events
export interface ClientCreatedPayload extends BaseEventPayload<Client> {
  eventType: 'client.created';
  data: Client;
}

export interface ClientUpdatedPayload extends BaseEventPayload {
  eventType: 'client.updated';
  data: {
    client: Client;
    changes: Partial<Client>;
  };
}

// Transaction Events
export interface TransactionCreatedPayload extends BaseEventPayload<SalesTransaction> {
  eventType: 'transaction.created';
  data: SalesTransaction;
}

export interface TransactionApprovedPayload extends BaseEventPayload<SalesTransaction> {
  eventType: 'transaction.approved';
  data: SalesTransaction;
}

// Commission Events
export interface CommissionCalculatedPayload extends BaseEventPayload {
  eventType: 'commission.calculated';
  data: {
    transaction: SalesTransaction;
    commissions: {
      account_manager?: {
        user_id: string;
        user_name: string;
        amount: number;
        rate: number;
      };
      closer?: {
        user_id: string;
        user_name: string;
        amount: number;
        rate: number;
      };
      sales_rep?: {
        user_id: string;
        user_name: string;
        amount: number;
        rate: number;
      };
    };
    totalCommission: number;
  };
}

// Xero Sync Events
export interface XeroInvoiceSyncedPayload extends BaseEventPayload {
  eventType: 'xero.invoice_synced';
  data: {
    xeroInvoice: unknown;
    crmOpportunity: Opportunity;
    mappingId: string;
  };
}

export interface XeroContactSyncedPayload extends BaseEventPayload {
  eventType: 'xero.contact_synced';
  data: {
    xeroContact: unknown;
    crmClient: Client;
    mappingId: string;
  };
}

export interface XeroSyncConflictPayload extends BaseEventPayload {
  eventType: 'xero.sync_conflict';
  data: {
    conflictType: 'field_mismatch' | 'data_mismatch' | 'deleted_entity' | 'duplicate_mapping';
    mappingId: string;
    xeroData: unknown;
    crmData: unknown;
    conflictingFields: string[];
    autoResolved: boolean;
    resolutionStrategy?: string;
  };
}

// Union type for all event payloads
export type CRMSalesEventPayload =
  | OpportunityCreatedPayload
  | OpportunityStageChangedPayload
  | OpportunityClosedWonPayload
  | ClientCreatedPayload
  | ClientUpdatedPayload
  | TransactionCreatedPayload
  | TransactionApprovedPayload
  | CommissionCalculatedPayload
  | XeroInvoiceSyncedPayload
  | XeroContactSyncedPayload
  | XeroSyncConflictPayload
  | BaseEventPayload;

// ============================================================================
// EVENT HANDLER TYPES
// ============================================================================

export type EventHandler<T extends BaseEventPayload = BaseEventPayload> = (
  payload: T
) => void | Promise<void>;

export type OpportunityEventHandler = EventHandler<OpportunityCreatedPayload | OpportunityStageChangedPayload | OpportunityClosedWonPayload>;
export type ClientEventHandler = EventHandler<ClientCreatedPayload | ClientUpdatedPayload>;
export type TransactionEventHandler = EventHandler<TransactionCreatedPayload | TransactionApprovedPayload>;
export type CommissionEventHandler = EventHandler<CommissionCalculatedPayload>;
export type XeroEventHandler = EventHandler<XeroInvoiceSyncedPayload | XeroContactSyncedPayload | XeroSyncConflictPayload>;

// ============================================================================
// EVENT LOG INTERFACE (Database Schema)
// ============================================================================

export interface CRMSalesEventLog {
  id: string;
  event_type: CRMSalesEventType;
  event_data: unknown;
  source_service: string;
  source_entity_type?: string;
  source_entity_id?: string;
  target_services?: string[];
  processing_status: 'pending' | 'processed' | 'failed' | 'retrying';
  processed_at?: string;
  retry_count: number;
  error_message?: string;
  created_at: string;
  actor_id?: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// EVENT CONFIGURATION
// ============================================================================

export interface EventConfig {
  enabled: boolean;
  async: boolean;
  retryable: boolean;
  maxRetries: number;
  timeout: number;
  priority: 'low' | 'normal' | 'high' | 'critical';
}

export const EVENT_CONFIGS: Record<CRMSalesEventType, EventConfig> = {
  // Opportunity Events
  'opportunity.created': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'opportunity.updated': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'opportunity.stage_changed': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'high',
  },
  'opportunity.closed_won': {
    enabled: true,
    async: false, // Synchronous for critical path
    retryable: true,
    maxRetries: 5,
    timeout: 10000,
    priority: 'critical',
  },
  'opportunity.closed_lost': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'opportunity.assigned': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'opportunity.deleted': {
    enabled: true,
    async: true,
    retryable: false,
    maxRetries: 0,
    timeout: 5000,
    priority: 'normal',
  },

  // Client Events
  'client.created': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'client.updated': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'client.assigned': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'client.deleted': {
    enabled: true,
    async: true,
    retryable: false,
    maxRetries: 0,
    timeout: 5000,
    priority: 'normal',
  },

  // Transaction Events
  'transaction.created': {
    enabled: true,
    async: false, // Synchronous for commission calculation
    retryable: true,
    maxRetries: 5,
    timeout: 10000,
    priority: 'critical',
  },
  'transaction.updated': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'transaction.approved': {
    enabled: true,
    async: false,
    retryable: true,
    maxRetries: 5,
    timeout: 10000,
    priority: 'high',
  },
  'transaction.rejected': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'transaction.paid': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'high',
  },

  // Commission Events
  'commission.calculated': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'high',
  },
  'commission.approved': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'high',
  },
  'commission.paid': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'high',
  },

  // Xero Sync Events
  'xero.invoice_synced': {
    enabled: true,
    async: false, // Synchronous to trigger CRM update
    retryable: true,
    maxRetries: 5,
    timeout: 10000,
    priority: 'high',
  },
  'xero.contact_synced': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 5,
    timeout: 10000,
    priority: 'normal',
  },
  'xero.payment_synced': {
    enabled: true,
    async: true,
    retryable: true,
    maxRetries: 3,
    timeout: 5000,
    priority: 'normal',
  },
  'xero.sync_conflict': {
    enabled: true,
    async: true,
    retryable: false,
    maxRetries: 0,
    timeout: 5000,
    priority: 'normal',
  },
  'xero.sync_completed': {
    enabled: true,
    async: true,
    retryable: false,
    maxRetries: 0,
    timeout: 5000,
    priority: 'low',
  },
};
